import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Radio, Tooltip, NumberInput } from '@mantine/core';
import { translate } from '@shakkerai/util-i18n';
import { Icon } from '@shakkerai/components';
import { getUnitData } from '@starflow/app/helpers/flow';
import { getSharedData, getTempState, setTempState, useTempState, useUnitData } from '@starflow/app/store';
import { PickerPanel, toHex } from '@shakkerai/pro-atoms';
import { SubWindow } from '../../../../components/common/Window';
import { CommonDropZone } from './commonDropzone';
import { updateUnitData } from '../../../../helpers/flow';
import { getImageColorPalette } from '@shakkerai/services/src/pro';
import { getImageOssPath } from '@shakkerai/pro-utils';
import { NumberInputGroup } from '@shakkerai/pro-components';
import { isOverseas } from '@shakkerai/pro-utils';
import { shuffleArray } from '@shakkerai/pro-utils';
import { useUpdateEffect } from 'ahooks';

const COLOR_BLOCK_Limit = 5;
const COLORS_UPDATE_TYPE = {
  singleDelte: 1,
  deleteAll: 2,
  singleChange: 3,
  singleAdd: 4,
  changeAll: 5,
  deleteIndex: 6,
};
const ColorOptionWindowLabel = 'colorReference-options';
const ColorPickerWindowLabel = 'colorReference-color-picker';
const DefaultColors = [
  ['#EDD0B7', '#EBC598', '#E6865D', '#6E664D', '#3D2423'],
  ['#F2F2F1', '#e48b8b', '#dbe7e3', '#ac3c3b', '#e4a34b'],
  ['#1B0D1E', '#463338', '#d23e3e', '#4b9b8b', '#3c6c6c'],
  ['#d4c778', '#9cd4cc', '#F1C37E', '#d4644c', '#ec8b64'],
  ['#d7e3e7', '#738393', '#d46353', '#8ca4a4', '#EEF7FC'],
  ['#a0dce8', '#e8f0a0', '#f4fcfc', '#ABE5F2', '#ccecb4'],
];

const updateColors = (type, value, originColors, needReplaceColorIndex) => {
  let newColors = [];
  switch (type) {
    case COLORS_UPDATE_TYPE.singleDelte:
      newColors = originColors.filter((color) => color !== value);
      break;
    case COLORS_UPDATE_TYPE.deleteIndex:
      newColors = [...originColors];
      newColors.splice(value, 1);
      break;
    case COLORS_UPDATE_TYPE.deleteAll:
      newColors = [];
      break;
    case COLORS_UPDATE_TYPE.singleChange:
      newColors = originColors.map((color, index) => (index === needReplaceColorIndex ? value : color));
      break;
    case COLORS_UPDATE_TYPE.singleAdd:
      newColors = [...originColors];
      newColors[needReplaceColorIndex] = value;
      break;
    case COLORS_UPDATE_TYPE.changeAll:
      if (Array.isArray(value)) newColors = [...value];
      break;
    default:
      newColors = [...originColors];
      break;
  }
  return newColors;
};

const ColorItem = ({
  color,
  className,
  openColorOptionWindow,
  selected,
  setSelected,
  handleUpdateColor,
  editable,
  index,
  ...rest
}) => {
  return (
    <div className={`${selected ? 'border-[#339BFF] border rounded-sm' : ''}`}>
      <div
        className={`${className} w-5 h-5 rounded-sm colorItem group relative ${
          selected ? 'border border-[#FFFFFF]' : ''
        }`}
        style={{ backgroundColor: color }}
        {...rest}
      >
        {editable && (
          <span
            onClick={(e) => {
              e?.stopPropagation();
              handleUpdateColor?.(COLORS_UPDATE_TYPE.deleteIndex, index);
              setSelected(null);
            }}
            icon={'icon-border_delete'}
            className="group-hover:flex w-3 h-3 text-[18px] leading-3 absolute -top-[4px] -right-[4px] rounded-full hidden items-center justify-center bg-[#FFF] border border-[#ededed]"
          >
            -
          </span>
        )}
      </div>
    </div>
  );
};

const IconButton = React.memo((props) => {
  const { onClick, disabled, icon, className, ...rest } = props;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${className} flex items-center justify-center rounded-sm text-[#7A7A7A] disabled:opacity-30`}
    >
      <Icon className={icon || 'icon-prozhankai'} fontSize={12} color="#666" />
    </button>
  );
});

const ColorBlocks = React.memo((props) => {
  const {
    colors,
    handleUpdateColor,
    changeFoldState,
    openColorOptionWindow,
    handleSingleColorClick,
    disableAction,
    onStrengthChange,
    generator,
    referenceColorSelectedIndex,
    setReferenceColorSelectedIndex,
  } = props;

  const colorControl = generator?.data?.colorControl;

  const [editable, setEditable] = useState(false);

  const inputRef = useRef(null);

  const handlekeyUp = useCallback((e) => {
    if (e.key === 'Enter') {
      inputRef.current?.blur();
    }
  }, []);

  const handleChangeWeight = useCallback(
    (weight) => {
      try {
        const value = Number(weight.toFixed(2));
        onStrengthChange?.(value, generator?.id);
      } catch (e) {}
    },
    [generator?.id, onStrengthChange],
  );

  const handleClear = () => {
    if (colors?.length) handleUpdateColor(COLORS_UPDATE_TYPE.deleteAll);
    changeFoldState?.();
  };

  return (
    <div className="flex items-center justify-between gap-2 h-7 mt-3 color-blocks">
      <div
        className={`${
          disableAction ? 'pointer-events-none cursor-not-allowed' : ''
        } flex items-center justify-center w-full h-full  border-[#F4F6F8] rounded-sm border`}
        onClick={() => {
          openColorOptionWindow();
        }}
      >
        {colors.map((color, index, array) => {
          return (
            <div
              className={`grow basis-0 h-full ${index === 0 ? 'rounded-l-sm' : ''} ${
                index === array.length - 1 ? 'rounded-r-sm' : ''
              } ${referenceColorSelectedIndex === index ? 'border border-[#339BFF]' : ''}`}
              style={{ backgroundColor: color }}
              onClick={() => {
                setReferenceColorSelectedIndex(index);
              }}
            >
              {referenceColorSelectedIndex === index && <div className="h-full w-full border border-[#FFFFFF]"></div>}
            </div>
          );
        })}
        {/* {colors?.length < COLOR_BLOCK_Limit && !disableAction && (
          <IconButton onClick={openColorOptionWindow} className="w-7 h-7 bg-[#F4F6F8]" />
        )} */}
        {/* {!!colors?.length && !disableAction && (
          <IconButton
            onClick={() => handleUpdateColor(COLORS_UPDATE_TYPE.singleDelte, colors?.pop())}
            icon={'icon-border_delete'}
            className="w-7 h-7 bg-[#F4F6F8]"
          />
        )} */}
      </div>
      <div className="flex-1 flex justify-end items-center gap-2">
        {!!colors?.length && (
          <Tooltip withArrow position="bottom" className="translate-y-[-5px] scale-90" label={translate('cn_weight')}>
            <NumberInput
              size="xxs"
              step={0.1}
              min={0}
              max={1}
              ref={inputRef}
              readOnly={!editable}
              value={colorControl?.colorStrength || 0.65}
              onKeyUp={handlekeyUp}
              onClick={() => setEditable(true)}
              onBlur={() => setEditable(false)}
              onChange={handleChangeWeight}
              className="cn-weight-input"
            />
          </Tooltip>
        )}
        <IconButton
          className="w-[18px] h-[18px] hover:bg-[#f2f4f6] color-control-button"
          onClick={handleClear}
          icon={'icon-proshouqi'}
        />
      </div>
    </div>
  );
});

const ColorPickerModal = React.memo(
  ({ value = '#FFFFFF', onChange, onClose, position, handleCustomModalClose, windowId }) => {
    return (
      <SubWindow
        className="w-[275px] colorReference_picker_modal"
        name={ColorPickerWindowLabel}
        title={translate('color_para_title3')}
        // disableHeader
        positionSetter={() => ({ x: position.x - 275 || 0, y: position.y || 48 })}
        handleCustomClose={handleCustomModalClose}
        windowId={windowId}
      >
        <PickerPanel
          color={value}
          format="hex"
          onChange={(color) => {
            // 后端这里要求使用hex，不支持rgba字符串
            onChange(toHex(color));
          }}
          onClose={onClose}
        />
      </SubWindow>
    );
  },
);

const ColorControlModal = React.memo(
  ({
    disabled,
    generator,
    isInpaint,
    position,
    colors,
    handleUpdateColor,
    handleUpdateSingleColor,
    handleSingleColorClick,
    handleCustomModalClose,
    handleUploadImage,
    needReplaceColor,
    colorsFromImg,
    sourceImage,
    handleUpdateColorSource,
    onStrengthChange,
    referenceColorSelectedIndex,
    setReferenceColorSelectedIndex,
    presetColorSelectedIndex,
    setPresetColorSelectedIndex,
  }) => {
    const [defaultColorCheckIndex, setDefaultColorCheckIndex] = useState(9999);
    const defaultColorCheckIndexRef = useRef(9999);
    // 预设色值不能影响用户自定义色值
    const [oldColors, setOldColors] = useState(colors);
    const openedSubWindow = useTempState('exclusiveWindow');
    const colorControl = generator?.data?.colorControl;
    const { presetColorRandomArr, presetColorRandomArrPointer } = useTempState();

    const getColorPickerPosition = () => {
      const modalElement = document.querySelector('.colorReference_modal');
      const modalPosition = modalElement?.getBoundingClientRect();
      const position = {
        x: modalPosition.x > 275 ? modalPosition.x : modalPosition.x + modalPosition.width + 275,
        y: modalPosition.y,
      };
      return position;
    };

    const handleChangeWeight = useCallback(
      (value) => {
        onStrengthChange(Number(value.toFixed(2)), generator?.id);
      },
      [generator?.id, onStrengthChange],
    );

    const handleReplaceColors = (colorSet, isUseAI = false) => {
      handleUpdateColorSource(false);
      handleUpdateColor(COLORS_UPDATE_TYPE.changeAll, isUseAI ? colorsFromImg : colorSet);
    };

    const handleAddButtonClick = async () => {
      if (oldColors.length >= COLOR_BLOCK_Limit) return;
      handleSingleColorClick?.('', true);
      handleUpdateSingleColor('#FFFFFF', true);
      const currentLength = oldColors.length;
      await new Promise((resolve) => requestAnimationFrame(resolve));
      setReferenceColorSelectedIndex(currentLength);
    };

    // update customColor
    useEffect(() => {
      if (defaultColorCheckIndexRef.current !== 9999) return;
      setOldColors(colors);
    }, [colors]);

    // use Default
    useEffect(() => {
      if (defaultColorCheckIndex === 9999) return;
      defaultColorCheckIndexRef.current = defaultColorCheckIndex;
      handleUpdateColor(COLORS_UPDATE_TYPE.changeAll, DefaultColors[defaultColorCheckIndex]);
    }, [defaultColorCheckIndex]);

    return (
      <>
        <SubWindow
          className="w-[253px] colorReference_modal"
          name={ColorOptionWindowLabel}
          title={translate('color_title')}
          positionSetter={() => ({ x: position?.x - 253, y: 48 })}
          handleCustomClose={handleCustomModalClose}
        >
          <div className="px-4 pt-3 pb-4 border-t border-[#EFEFEF]">
            <div className="flex-row flex items-center gap-1 mb-3">
              {oldColors?.map((color, index) => (
                <ColorItem
                  index={index}
                  handleUpdateColor={handleUpdateColor}
                  selected={index === referenceColorSelectedIndex}
                  setSelected={setReferenceColorSelectedIndex}
                  editable={true}
                  className="w-[41px] h-[41px] cursor-pointer border"
                  color={color}
                  key={`${color}-${index}`}
                  onClick={() => {
                    setReferenceColorSelectedIndex(index);
                    handleSingleColorClick?.(color, true, index);
                  }}
                />
              ))}
              {oldColors?.length < COLOR_BLOCK_Limit && (
                <IconButton
                  className="w-[41px] h-[41px] border border-[#D6D7DB] text-[#666666] ml-[0.5] addColor_custom"
                  onClick={handleAddButtonClick}
                />
              )}
            </div>
            <CommonDropZone
              disabledTip={isInpaint ? translate('cn_source_referece_only_tip') : null}
              disabled={disabled || isInpaint}
              generator={generator}
              allowViewSourceImage={isInpaint}
              onImageUpload={handleUploadImage}
              sourceImage={sourceImage?.url}
              uploadingKey={`colorReference`}
              emptyPlaceholder={
                <div className="flex items-center justify-center flex-col gap-2">
                  <span className="text-sm text-[#2D2D2D]">{translate('color_upload_para1')}</span>
                  <span className="text-sm text-[#adadad]">{translate('imgupload_placeholder')}</span>
                </div>
              }
            />

            <div className="flex items-center mt-2 border-[#D6D7DB]">
              <span className="text-[#2d2d2d] font-grotesk text-xs">{translate('cn_weight')}</span>
              <div className="w-[150px] flex items-center ml-2 grow">
                <NumberInputGroup
                  size="xs"
                  step={0.05}
                  min={0}
                  max={1}
                  inputWidth={42}
                  disabled={disabled}
                  value={colorControl?.colorStrength ?? 0.65}
                  onChange={handleChangeWeight}
                />
              </div>
            </div>
          </div>
          <div className="text-xs px-4 pb-4 pt-3 border-t border-[#EFEFEF]">
            <div className="flex flex-row justify-between mb-2">
              <div className="font-semibold">{translate('color_para_title2')}</div>
              <div
                className="cursor-pointer"
                onClick={() => {
                  const newPointer = (presetColorRandomArrPointer + 4) % presetColorset.length;
                  setPresetColorSelectedIndex(null);
                  setTempState({
                    presetColorRandomArrPointer: newPointer,
                  });
                }}
              >
                {translate('randomize')}
              </div>
            </div>
            <div className="w-full flex gap-2 flex-col">
              {presetColorRandomArr
                .reduce((accu, cur, index, array) => {
                  if (index < presetColorRandomArrPointer || index >= presetColorRandomArrPointer + 4) {
                    return accu;
                  } else {
                    if (index === array.length - 1) {
                      if (accu.length < 3) {
                        return [...array.slice(0, 3 - accu.length), ...accu, cur];
                      } else {
                        return [...accu, cur];
                      }
                    } else {
                      return [...accu, cur];
                    }
                  }
                }, [])
                .map((colorSet, index) => {
                  return (
                    <div className={`${presetColorSelectedIndex == index ? 'border border-[#339BFF]' : ''}`}>
                      <div
                        className={`overflow-hidden rounded-sm w-full flex flex-row h-[30px] cursor-pointer ${
                          presetColorSelectedIndex == index ? 'rounded-sm border border-[#F4F6F8]' : ''
                        }`}
                        onClick={() => {
                          setPresetColorSelectedIndex(index);
                          setReferenceColorSelectedIndex(null);
                          handleReplaceColors(colorSet);
                        }}
                      >
                        {colorSet.map((color) => {
                          return <div style={{ backgroundColor: color }} className="grow" />;
                        })}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </SubWindow>
        {openedSubWindow === ColorPickerWindowLabel && (
          <ColorPickerModal
            value={needReplaceColor}
            position={getColorPickerPosition()}
            onChange={handleUpdateSingleColor}
            handleCustomModalClose={handleCustomModalClose}
            windowId={`${ColorOptionWindowLabel}-${generator?.id}`}
            onClose={() =>
              setTempState({
                exclusiveWindow: `${ColorOptionWindowLabel}-${generator?.id}`,
              })
            }
          />
        )}
      </>
    );
  },
);

export const ColorReference = React.memo(({ disabled, generator = {} }) => {
  const unitData = useUnitData();
  const mode = getTempState('globalMode');
  const isSimple = mode === 'simple';
  const isInpaint = generator?.type === 'inpaint';
  const baseType = generator.data.baseType;
  const colors = generator?.data?.colorControl?.colorPalette || [];
  const [colorfoldState, setColorFoldState] = useState(false);
  const [colorPickIsOpenedByModal, setColorPickIsOpenedByModal] = useState(false);
  const [colorsFromImg, setColorsFromImg] = useState([]);
  const [referenceImg, setReferenceImg] = useState();
  const [isCheckedDefaultColor, setIsCheckedDefaultColor] = useState(false);
  const needReplaceColorIndexRef = useRef(-1);
  const openedSubWindow = useTempState('exclusiveWindow');
  const [referenceColorSelectedIndex, setReferenceColorSelectedIndex] = useState(null);
  const [presetColorSelectedIndex, setPresetColorSelectedIndex] = useState(null);

  const IS_STARTFLOW_MODEL = useMemo(() => {
    if (unitData?.presetBaseModelId === 'base_checkpoint') return true;
    return false;
  }, [unitData]);

  const getModalPosition = () => {
    const colorReferenceElement = document.querySelector('#colorReference');
    const modalPosition = colorReferenceElement?.getBoundingClientRect();
    const position = { x: modalPosition?.x - 16 || 0, y: 48 };
    // const position = { x: modalPosition?.x - 16 || 0, y: modalPosition?.y }
    return position;
  };

  const openedColorOptions = useMemo(() => {
    if (openedSubWindow === ColorPickerWindowLabel && colorPickIsOpenedByModal) {
      return true;
    }
    return openedSubWindow?.includes(ColorOptionWindowLabel)
      ? openedSubWindow.split(`${ColorOptionWindowLabel}-`)[1]
      : null;
  }, [openedSubWindow, ColorPickerWindowLabel, colorPickIsOpenedByModal]);

  // 允许colorControl、colorPciker同时存在
  // const enableOpenColorOptionModal = useMemo(() => {
  //   return (
  //     openedSubWindow === ColorOptionWindowLabel ||
  //     (openedSubWindow === ColorPickerWindowLabel && colorPickIsOpenedByModal)
  //   );
  // }, [openedSubWindow, colorPickIsOpenedByModal, ColorOptionWindowLabel, ColorPickerWindowLabel]);

  const openOptionModal = useCallback(
    () =>
      setTempState({
        exclusiveWindow: `${ColorOptionWindowLabel}-${generator?.id}`,
      }),
    [generator],
  );

  const addColorControl = () => {
    if (colors.length === 0) {
      // 随机选一个颜色添加进去
      // const randomIndex = Math.floor(Math.random() * presetColorset.length);
      setReferenceColorSelectedIndex(null);
      updateUnitData(generator.id, {
        colorControl: {
          ...(generator?.data?.colorControl || {}),
          colorPalette: presetDefaultColor,
        },
      });
    }
    // setIsCheckedDefaultColor(false);
    setColorFoldState(true);
    openOptionModal();
  };

  // 展开收起状态
  const toggleColorPicker = () => {
    setIsCheckedDefaultColor(false);
    setColorFoldState((prev) => !prev);
  };

  // 更新colors
  const handleUpdateColor = useCallback(
    (type, value, needReplaceColorIndex) => {
      const newColors = updateColors(type, value, colors, needReplaceColorIndex);
      // console.log('xxx',generator.id)
      updateUnitData(generator.id, {
        colorControl: {
          ...(generator?.data?.colorControl || {}),
          colorPalette: newColors,
        },
      });
    },
    [colors, generator],
  );

  const handleColorStrengthChange = (val, id) => {
    if (val && id) {
      updateUnitData(id, {
        colorControl: {
          ...(generator?.data?.colorControl || {}),
          colorStrength: val,
        },
      });
    }
  };
  // 切换独立单元的时候重新控制色彩按钮
  useEffect(() => {
    const _colors = getUnitData(generator.id)?.colorControl?.colorPalette || [];
    if (_colors.length > 0) {
      setColorFoldState(true);
    } else {
      setColorFoldState(false);
    }
    if (openedSubWindow?.includes(ColorOptionWindowLabel)) {
      setTempState({
        exclusiveWindow: null,
      });
    }
  }, [generator.id]);
  useEffect(() => {
    if (colors.length === 0) {
      setColorFoldState(false);
    } else {
      setColorFoldState(true);
    }
  }, [colors]);

  useEffect(() => {
    if (openedColorOptions) {
      setColorFoldState(true);
    }
  }, [openedColorOptions]);
  useEffect(() => {
    if (generator.data.colorControl?.colorPalette?.length === 0) {
      updateUnitData(generator.id, {
        colorReferenceImg: '',
      });
      setReferenceImg(null);
    }
  }, [generator.data.colorControl?.colorPalette]);
  // 通过changeType更新Colors
  const handleUpdateSingleColor = useCallback(
    (singleColor, isAdd = false) => {
      if (!singleColor) return;
      handleUpdateColor(
        isAdd ? COLORS_UPDATE_TYPE.singleAdd : COLORS_UPDATE_TYPE.singleChange,
        singleColor,
        needReplaceColorIndexRef.current,
      );
    },
    [handleUpdateColor],
  );

  // 控制colorPicker、colorControl同时开启，记录点击的色值index
  const handleSingleColorClick = (singleColor, status = false, index) => {
    setTempState({
      exclusiveWindow: ColorPickerWindowLabel,
    });
    setColorPickIsOpenedByModal(status);
    // singleColor ？ update ： add
    const newColorIndex =
      typeof index !== 'undefined'
        ? index
        : singleColor
          ? colors.findIndex((color) => color === singleColor)
          : colors.length < COLOR_BLOCK_Limit && colors.length;
    needReplaceColorIndexRef.current = newColorIndex;
    // 不需要在关闭时重置，每次开启拾色器的操作都会进入这个方法中重置index
    // setNeedReplaceColorIndex(newColorIndex);
  };

  const handleColorReferenceImgChange = useCallback(async (file) => {
    /*
      todos fqy 如果需要保存颜色参考图，这里需要调一下updateUnitData
      把url存到 colorReferenceImg 这个字段里
    */
    try {
      setReferenceImg(file);
      let imgId = file?.file;
      if (!imgId) {
        imgId = getImageOssPath(file?.url);
      }
      if (file) {
        const { colorPalette } = await getImageColorPalette(imgId);
        if (Array.isArray(colorPalette)) {
          setColorsFromImg(colorPalette);
          handleUpdateColor(COLORS_UPDATE_TYPE.changeAll, colorPalette);
          setPresetColorSelectedIndex(null);
        }
      }
    } catch (error) {
      console.error('Failed to process image:', error);
    }
  }, []);

  const handleCustomModalClose = (event) => {
    if (getSharedData('dragging-element')) return;
    if (event.target?.closest('.color-control-button')) {
      return;
    }
    if (event.target?.closest('.color-blocks')) {
      return;
    }
    // 点击色块切换拾色器
    if (event.target?.closest('.colorReference_modal') && event.target.closest('.colorItem')) {
      // console.log('xxx 1')
      return;
    }
    // 仅打开拾色器，点击空白处
    if (!openedColorOptions) {
      // console.log('xxx 2')
      return setTempState({ exclusiveWindow: null });
    }
    // colorControl中新增色块
    if (event.target?.closest('.colorReference_modal') && event.target.closest('.addColor_custom')) {
      // console.log('xxx 3')
      return;
    }
    // 拾色器、colorControl同时开启，点击了colorControl
    if (event.target?.closest('.colorReference_modal')) {
      // console.log('xxx 4')
      return setTempState({ exclusiveWindow: `${ColorOptionWindowLabel}-${generator?.id}` });
    }
    // 拾色器、colorControl同时开启，点击了拾色器
    if (event.target?.closest('.colorReference_picker_modal')) {
      // console.log('xxx 5')
      return;
    }
    // 删空
    if (event.target.className.includes('icon-border_delete')) {
      // console.log('xxx 6')
      return;
    }
    // 点击空白处
    return setTempState({ exclusiveWindow: null });
  };

  const handleUpdateColorSource = (isDefaultColor) => setIsCheckedDefaultColor(isDefaultColor);

  // 从画布中选中图片作为参考
  useEffect(() => {
    // console.log('xxx',generator?.data?.colorReferenceImg)
    if (!generator?.data?.colorReferenceImg) return;
    handleColorReferenceImgChange({
      file: getImageOssPath(generator?.data?.colorReferenceImg),
      url: generator?.data?.colorReferenceImg,
    });
  }, [generator?.data?.colorReferenceImg]);
  useEffect(() => {
    let arr = shuffleArray([...Array(presetColorset.length).keys()]);
    setTempState({
      presetColorRandomArr: arr.map((index) => {
        return presetColorset[index];
      }),
    });
  }, []);

  return (
    <div className="relative" id="colorReference">
      <div className="flex items-center justify-between h-4 ">
        <h5 className="flex-1 text-[#2d2d2d] text-xs leading-[14px] font-bold font-gro">
          <span>{translate('color_title')}</span>
        </h5>
        <IconButton
          className="w-[18px] h-[18px] hover:bg-[#f2f4f6] color-control-button"
          onClick={addColorControl}
          disabled={colorfoldState}
        />
      </div>
      {colorfoldState && (
        <div className="cursor-pointer">
          <ColorBlocks
            colors={colors}
            handleUpdateColor={handleUpdateColor}
            handleUpdateSingleColor={handleUpdateSingleColor}
            changeFoldState={toggleColorPicker}
            openColorOptionWindow={openOptionModal}
            handleSingleColorClick={handleSingleColorClick}
            // disableAction={!IS_STARTFLOW_MODEL || isCheckedDefaultColor}
            generator={generator}
            onStrengthChange={handleColorStrengthChange}
            referenceColorSelectedIndex={referenceColorSelectedIndex}
            setReferenceColorSelectedIndex={setReferenceColorSelectedIndex}
          />
        </div>
      )}
      {openedColorOptions && (
        <ColorControlModal
          disabled={disabled}
          generator={generator}
          isInpaint={isInpaint}
          isSimple={isSimple}
          baseType={baseType}
          handleUploadImage={handleColorReferenceImgChange}
          key={ColorOptionWindowLabel}
          position={getModalPosition()}
          colors={colors}
          handleUpdateColor={handleUpdateColor}
          handleUpdateSingleColor={handleUpdateSingleColor}
          handleSingleColorClick={handleSingleColorClick}
          handleCustomModalClose={handleCustomModalClose}
          needReplaceColor={colors[needReplaceColorIndexRef.current]}
          sourceImage={referenceImg}
          colorsFromImg={colorsFromImg}
          handleUpdateColorSource={handleUpdateColorSource}
          onStrengthChange={handleColorStrengthChange}
          referenceColorSelectedIndex={referenceColorSelectedIndex}
          setReferenceColorSelectedIndex={setReferenceColorSelectedIndex}
          presetColorSelectedIndex={presetColorSelectedIndex}
          setPresetColorSelectedIndex={setPresetColorSelectedIndex}
        />
      )}
      {openedSubWindow === ColorPickerWindowLabel && !colorPickIsOpenedByModal && (
        <ColorPickerModal
          value={colors[needReplaceColorIndexRef.current]}
          position={getModalPosition()}
          onChange={handleUpdateSingleColor}
          onClose={() => setTempState({ exclusiveWindow: null })}
        />
      )}
    </div>
  );
});

const presetDefaultColor = ['#003049', '#D62828', '#F77F00', '#FCBF49', '#EAE2B7'];
const presetColorset = [
  ['#0B755F', '#14626E', '#A1C69B', '#E9D8D0', '#F15D8F'],
  ['#7892B7', '#91B5A9', '#D98481', '#E9BAAA', '#EECA80'],
  ['#146D59', '#97BEAC', '#D5E2D8', '#EF7329', '#F1A801'],
  ['#CF9168', '#E9BE91', '#F3AF28', '#F9E5A0', '#FFD253'],
  ['#837781', '#EBB1A3', '#EEA7AB', '#F1D0C7', '#F5B8BD'],
  ['#364A62', '#776B6B', '#91AFB1', '#DB8E74', '#E3B385'],
  ['#9A7057', '#B8B48E', '#C16E66', '#E5ACA5', '#E6D4CA'],
  ['#8391C3', '#BADCAD', '#DDDCD6', '#E49E61', '#F9C89B'],
  ['#4335A7', '#474E93', '#72BAA9', '#D5E7B5'],
  ['#001A6E', '#009990', '#074799', '#213555'],
  ['#3E5879', '#4C4B16', '#7E1891', '#8A8122'],
  ['#8D0B41', '#B9E5E8', '#BFECFF', '#CDC1FF'],
  ['#D39D55', '#D6CFB4', '#D8C4B6', '#E0F2EB'],
  ['#E1FFBB', '#E6C767', '#E73879', '#F26B0F'],
  ['#F5EFE7', '#F87A53', '#FCC737', '#FFB38E'],
  ['#FFCCEA', '#FFCF9D', '#FFF6E3', '#FFF8E6'],
  ['#0D92F4', '#133E87', '#31511E'],
  ['#5DB996', '#608BC1', '#76CDFF'],
  ['#799DBC', '#7ED4AD', '#859F3E'],
  ['#A1D6CB', '#C9E9D2', '#D76C82'],
  ['#D8C4B6', '#E3F0AF', '#F3F3E0'],
  ['#F6FCDF', '#F85453', '#FCF596'],
  ['#FF4545', '#FF8383', '#FF9C73'],
  ['#FFB38E', '#FFE3E2', '#FFF574'],
];
